import React from 'react';
import { Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ProductProps {
  id: number;
  name: string;
  slug: string;
  price: number;
  originalPrice: number;
  rating: number;
  reviews: number;
  image: string;
  tag?: string;
  description: string;
}

export const ProductCard: React.FC<ProductProps> = (props) => {
  const { id, name, slug, price, originalPrice, rating, reviews, image, tag, description } = props;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-transparent hover:border-simba-green/10 flex flex-col h-full"
    >
      
      {/* Image - full show, part of card */}
      <div className="relative h-72 flex items-center justify-center bg-simba-cream/50 p-4">
        <Link to={`/product/${slug}`} className="block w-full h-full flex items-center justify-center">
          <img 
            src={image} 
            alt={name} 
            className="max-w-full max-h-full w-auto h-auto object-contain transform group-hover:scale-105 transition-transform duration-500 ease-out"
            referrerPolicy="no-referrer"
          />
        </Link>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-grow">
        {tag && (
          <span className="text-xs font-bold text-simba-green uppercase tracking-wider mb-2 inline-block">{tag}</span>
        )}
        <div className="flex items-center space-x-1 mb-2">
          <div className="flex text-simba-gold">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={12} fill={i < rating ? "currentColor" : "none"} className={i < rating ? "text-simba-gold" : "text-gray-300"} />
            ))}
          </div>
          <span className="text-xs text-gray-400 font-medium">({reviews} reviews)</span>
        </div>

        <Link to={`/product/${slug}`} className="block group-hover:text-simba-green transition-colors">
          <h3 className="font-display font-bold text-lg text-simba-green leading-tight mb-2 group-hover:text-simba-dark transition-colors">
            {name}
          </h3>
        </Link>
        
        <p className="text-sm text-gray-500 font-body mb-4 line-clamp-2 flex-grow">
          {description}
        </p>

        <div className="pt-4 border-t border-gray-100 flex items-center justify-between mt-auto">
          <div className="flex flex-col">
            <span className="text-xs text-gray-400 line-through">£{originalPrice.toFixed(2)}</span>
            <span className="font-display font-bold text-xl text-simba-dark">£{price.toFixed(2)}</span>
          </div>
          
          <Link 
            to={`/product/${slug}`}
            className="flex items-center space-x-2 text-simba-green font-bold text-sm hover:text-simba-gold transition-colors group/btn z-20 cursor-pointer"
          >
            <span>View Product</span>
            <ArrowRight size={16} className="transform group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
